import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { getWeeklyLogs, getWeeklyLogsSilent, setCurrentDay } from 'store/container/weeklyLogs/weeklyLogs-slice';
import { TIME_ENTRY_WS_EVENT, isLocalEcho, TimeEntryWsEvent } from 'services/timeTracking';
import useWebSocket from 'utils/useWebSocket';
import { UserResponse } from 'types';
import { RootState } from 'store';

const DAY_CHECK_INTERVAL_MS = 60 * 1000;
// A timer swap emits two events back to back (paused old + started new). Coalesce
// them into a single refetch of the weekly totals to avoid redundant requests.
const WS_REFETCH_DEBOUNCE_MS = 300;

const getLocalDay = () => dayjs().format('YYYY-MM-DD');
const getLocalWeek = () => dayjs().startOf('week').format('YYYY-MM-DD');

const useWeeklyTimer = () => {
  const user: UserResponse = useSelector((store: any) => store.auth.user);
  const globalLog = useSelector((globalState: RootState) => globalState.globalLog);
  const weeklyLogs = useSelector((globalState: RootState) => globalState.weeklyLogs);
  const dispatch = useDispatch();

  const currentDayRef = useRef(getLocalDay());
  const currentWeekRef = useRef(getLocalWeek());
  const wsRefetchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const hasGlobalTriggerRunRef = useRef(false);

  // First load (and user switch) shows the loading state; subsequent local mutations
  // signalled via globalLog.trigger reconcile silently so the totals/list keep their
  // current state and scroll position instead of flashing a skeleton.
  useEffect(() => {
    dispatch(getWeeklyLogs());
  }, [user.uuid, dispatch]);

  useEffect(() => {
    if (!hasGlobalTriggerRunRef.current) {
      hasGlobalTriggerRunRef.current = true;
      return;
    }

    dispatch(getWeeklyLogsSilent());
  }, [globalLog.trigger, dispatch]);

  useEffect(() => {
    return () => {
      if (wsRefetchTimeoutRef.current) {
        clearTimeout(wsRefetchTimeoutRef.current);
      }
    };
  }, []);

  // Keep the header "Today" / "This week" totals in sync with time entries created,
  // edited or removed elsewhere (other devices, the task modal, timelog reports, etc.).
  useWebSocket({
    channelName: 'model.changes',
    listen: {
      event: TIME_ENTRY_WS_EVENT,
      callback: (event: TimeEntryWsEvent) => {
        if (isLocalEcho(event)) return;

        if (event.payload.user_uuid === user.uuid) {
          if (wsRefetchTimeoutRef.current) {
            clearTimeout(wsRefetchTimeoutRef.current);
          }

          wsRefetchTimeoutRef.current = setTimeout(() => {
            dispatch(getWeeklyLogsSilent());
          }, WS_REFETCH_DEBOUNCE_MS);
        }
      },
    },
  });

  useEffect(() => {
    const checkRollover = () => {
      const day = getLocalDay();
      const week = getLocalWeek();
      const dayChanged = day !== currentDayRef.current;
      const weekChanged = week !== currentWeekRef.current;

      if (!dayChanged && !weekChanged) {
        return;
      }

      currentDayRef.current = day;
      currentWeekRef.current = week;

      dispatch(setCurrentDay(day));

      if (weekChanged) {
        dispatch(getWeeklyLogs());
      }
    };

    const interval = setInterval(checkRollover, DAY_CHECK_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkRollover();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    // Window regaining focus (e.g. after the computer was locked/suspended).
    window.addEventListener('focus', checkRollover);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', checkRollover);
    };
  }, [dispatch]);

  return { weeklyLogs };
};

export default useWeeklyTimer;
